import React, { useEffect, useState } from "react";
import Table from "../../Table";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  SubscriptionReq,
  getSubscription,
} from "../../../utils/apis/accountsApi";
import { resetRebillPayments } from "../../../utils/apis/userApi";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LoaderOverlay from "../../../reusableComponents/LoaderOverlay/Index";
import { returnErrors } from "../../../utils/store/reducers/Error";
import { returnMessages } from "../../../utils/store/reducers/Message";
import ConfirmationModal from "../../../reusableComponents/ConfirmationModal";
import { formatDateInTimeZone } from "../../../utils/helpers/string";
import RePaymentModal from "../../../reusableComponents/RePaymentModal/RePaymentModal";
import { switchSubscription } from "../../../utils/apis/accountsApi";
import { setLastPayment } from "../../../utils/store/reducers/accountMetricsSlice";

export const Subscription = () => {
  const [subscription, setSubscription] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'unsubscribe' or 'cancel'

  const { idToken } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    page_size: 20,
  });
  const [openRepay, setOpenRepay] = useState(false);
  const [switchPayload, setSwitchPayload] = useState({
    subscription_id: null,
    payment_method: "nuvei",
  });
  const [isLoadingSwitch, setIsLoadingSwitch] = useState(false);
  const [openRebill, setOpenRebill] = useState(false);
  const [rebillPayload, setRebillPayload] = useState({
    payment_method: "coinpayment",
    account_id: null,
    payment_type: "rebill",
  });
  const [isLoadingRebill, setIsLoadingRebill] = useState(false);

  const handleSwitchSubscription = async () => {
    setIsLoadingSwitch(true);
    try {
      const response = await switchSubscription(switchPayload, idToken);
      if (response.status < 399) {
        dispatch(setLastPayment(response?.data?.data));
        window.location.replace(response?.data.url);
      }
    } catch (error) {
      console.error("error", error);
      dispatch(returnErrors(error.response?.data?.detail, 400));
    } finally {
      setIsLoadingSwitch(false);
    }
  };

  const handleRebillPayment = async () => {
    setIsLoadingRebill(true);
    try {
      const response = await resetRebillPayments(idToken, rebillPayload);
      if (response.status < 399) {
        dispatch(setLastPayment(response?.data?.data));
        window.location.replace(response?.data.url);
        setOpenRebill(false);
      } else {
        // Handle error response (API returns error.response instead of throwing)
        dispatch(
          returnErrors(
            response.data?.detail || "An error occurred during rebill",
            response.status || 400
          )
        );
        setOpenRebill(false);
      }
    } catch (error) {
      console.error("error", error);
      dispatch(
        returnErrors(
          error.response?.data?.detail || error.message || "An error occurred during rebill",
          error.response?.status || 400
        )
      );
      setOpenRebill(false);
    } finally {
      setIsLoadingRebill(false);
    }
  };

  const fetchSubscription = async () => {
    setIsLoading(true);
    try {
      const response = await getSubscription(idToken, params);

      setSubscription(response);
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, [JSON.stringify(params)]);

  const handleSubmit = async () => {
    if (!selectedSubscriptionId || !actionType) return;

    setIsLoadingConfirm(true);
    try {
      const data = { subscription_id: selectedSubscriptionId };
      if (actionType === "cancel") {
        data.is_delete = true;
      }

      const response = await SubscriptionReq(data, idToken);
      if (response.status < 399) {
        fetchSubscription();
        dispatch(returnMessages(response.data.detail, 200));
      }
    } catch (error) {
      console.error("error", error);
      dispatch(returnErrors(error.response.data.detail, 400));
    } finally {
      setIsLoadingConfirm(false);
      setShowConfirmationModal(false);
      setSelectedSubscriptionId(null);
      setActionType(null);
    }
  };

  const columns = [
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Account</span>
        </div>
      ),
      accessor: "account",
      Cell: ({ value }) => (
        <div className="col-wrapper">
          <p>{value}</p>
        </div>
      ),
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Level</span>
        </div>
      ),
      accessor: "level",
      Cell: ({ value }) => (
        <div className="col-wrapper">
          <p>{value}</p>
        </div>
      ),
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Payment Id</span>
        </div>
      ),
      accessor: "payment_id",
      Cell: ({ value }) => (
        <div className="col-wrapper">
          <p>{value}</p>
        </div>
      ),
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Payment Method</span>
        </div>
      ),
      accessor: "payment_mode",
      Cell: ({ value }) => (
        <div className="col-wrapper">
          <p style={{ textTransform: "capitalize" }}>{value || "N/A"}</p>
        </div>
      ),
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Start Date</span>
        </div>
      ),
      accessor: "start_date",
      Cell: ({ value }) => (
        <div className="col-wrapper">
          <p>{formatDateInTimeZone(value)}</p>
        </div>
      ),
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Next Billing Date</span>
        </div>
      ),
      accessor: "next_billing_date",
      Cell: ({ value }) => (
        <div className="col-wrapper">
          <p>{formatDateInTimeZone(value)}</p>
        </div>
      ),
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Subscription Start Date</span>
        </div>
      ),
      accessor: "subscription_start_date",
      Cell: ({ value }) => (
        <div className="col-wrapper">
          <p>{formatDateInTimeZone(value)}</p>
        </div>
      ),
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Account Status</span>
        </div>
      ),
      accessor: "account_status",
      Cell: ({ value }) => {
        const status = value?.toLowerCase()?.replace("_", " ");
        return (
          <div className="col-wrapper">
            <p
              className={`comp_status ${status === "failed"
                ? "red_status"
                : status === "passed"
                  ? "green_status"
                  : status === "in progress"
                    ? "yellow_status"
                    : "closed_status"
                }`}
            >
              {["failed", "passed", "in progress"].includes(status)
                ? status
                : "Closed"}
            </p>
          </div>
        );
      },
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Subscription Status</span>
        </div>
      ),
      accessor: "is_active",
      Cell: ({ value }) => (
        <div className="col-wrapper">
          <p
            className={`comp_status ${value === false ? "red_status" : value === true && "green_status"
              }`}
          >
            {value ? "Active" : "Inactive"}
          </p>
        </div>
      ),
    },
    {
      Header: () => (
        <div className="header-with-icon">
          <span>Action</span>
        </div>
      ),
      accessor: "id",
      Cell: ({ row }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleClick = (event) => {
          setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        const openConfirmationModal = (type) => {
          setSelectedSubscriptionId(row.original.id);
          setActionType(type);
          setShowConfirmationModal(true);
          handleClose();
        };

        return (
          <div className="col-wrapper">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                >
                  <MoreVertIcon sx={{ color: "white" }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={open}
              onClose={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  minWidth: "150px",
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={() => openConfirmationModal("unsubscribe")}>
                Unsubscribe
              </MenuItem>
              <MenuItem onClick={() => openConfirmationModal("cancel")}>
                Cancel and close Immediately
              </MenuItem>
              {row.original.payment_mode?.toLowerCase() === "coinpayment" && (
                <MenuItem
                  onClick={() => {
                    handleClose();
                    setRebillPayload((prev) => ({
                      ...prev,
                      account_id: row.original.login_id,
                    }));
                    setOpenRebill(true);
                  }}
                >
                  Rebill
                </MenuItem>
              )}
              {row.original.payment_mode === "stripe" && (
                <MenuItem
                  onClick={() => {
                    handleClose();
                    setSwitchPayload((prev) => ({
                      ...prev,
                      subscription_id: row.original.id,
                    }));
                    setOpenRepay(true);
                  }}
                >
                  Switch Subscription
                </MenuItem>
              )}
            </Menu>

            {
              showConfirmationModal && (
                <ConfirmationModal
                  handleConfirm={handleSubmit}
                  isLoading={isLoadingConfirm}
                  handleCancel={() => setShowConfirmationModal(false)}
                  title={
                    actionType === "cancel"
                      ? "Cancel and close immediately"
                      : "Unsubscribe"
                  }
                  msg={
                    actionType === "cancel"
                      ? "Are you sure you want to perform this action? This will close your account IMMEDIATELY."
                      : "Are you sure you want to complete this action? This will close your account at the end of the billing cycle."
                  }
                />
              )
            }
          </div >
        );
      },
    },
  ];

  return (
    <div>
      <RePaymentModal
        open={openRepay}
        setOpen={setOpenRepay}
        handleSubmit={handleSwitchSubscription}
        isLoading={isLoadingSwitch}
        setPayload={setSwitchPayload}
        payload={switchPayload}
        heading="Switch Subscription"
        isCoupon={false}
      />
      <RePaymentModal
        open={openRebill}
        setOpen={setOpenRebill}
        handleSubmit={handleRebillPayment}
        isLoading={isLoadingRebill}
        setPayload={setRebillPayload}
        payload={rebillPayload}
        heading="Rebill"
        isCoupon={false}
      />
      <div className="ar-table">
        {isLoading && <LoaderOverlay />}
        <Table
          columns={columns}
          data={subscription || []}
          setParams={setParams}
          params={params}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
